import { forwardRef, useId, type ReactNode } from 'react'
import { Input } from 'antd'
import type { InputRef as AntInputRef } from 'antd'
import { RequiredMark, RequiredCaption } from '@/shared/ui/atoms/required-mark'
import './input-search.css'

const { Search } = Input

export interface InputSearchProps {
  label?: string
  placeholder?: string
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  height?: string
  width?: string
  suffix?: ReactNode
  prefix?: ReactNode
  status?: string
  required?: boolean
  errorData?: Record<string, string | null | undefined>
  field?: string
  onPressEnter?: () => void
  onSearch?: (value: string) => void
  loading?: boolean
  disabled?: boolean
  name?: string
  id?: string
  /**
   * Application-internal search defaults to `autoComplete='off'` because
   * browser autofill suggestions are inappropriate (e.g. offering email
   * addresses in a SKU search). Override only if the search backs a true
   * identity field.
   */
  autoComplete?: string
  'aria-label'?: string
}

export const InputSearch = forwardRef<AntInputRef, InputSearchProps>(function InputSearch(
  {
    label,
    placeholder,
    value,
    onChange,
    height = '40px',
    width = '100%',
    suffix,
    prefix,
    status,
    required,
    errorData,
    field,
    onPressEnter,
    id: idProp,
    name,
    autoComplete = 'off',
    'aria-label': ariaLabel,
    ...rest
  },
  ref,
) {
  const fallbackId = useId()
  const id = idProp ?? fallbackId
  const accessibleName = ariaLabel ?? label ?? placeholder
  return (
    <div className='flex flex-col gap-2 relative w-full' style={{ width }}>
      {label && (
        <label htmlFor={id} className='text-xs font-medium text-(--quaternary-font-color)'>
          {label}
          {required && <RequiredMark />}
        </label>
      )}
      <Search
        ref={ref}
        id={id}
        name={name ?? id}
        autoComplete={autoComplete}
        aria-label={accessibleName}
        className='glasspro-input-search'
        styles={{ root: { width, height } }}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        suffix={suffix}
        prefix={prefix}
        status={status as 'error' | 'warning' | undefined}
        onPressEnter={onPressEnter}
        {...rest}
      />
      {status === 'error' && errorData && (
        <RequiredCaption errorData={errorData} field={field || label || ''} />
      )}
    </div>
  )
})
